import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, Tooltip, Stack, Divider, List, ListItemButton, ListItemText } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { getUserData } from "@/utils/userData";
import { useThemeMode } from "@/contexts/ThemeContext";
import { escapeHtml } from "@/utils/escapeHtml";

// Acces hardcodat: doar admin de nivel 2, sau username-urile explicite de mai jos
// (colaboratorul poate sa nu aiba admin=2, dar tot trebuie sa vada textele).
const ALLOWED_USERNAMES = ["oanabratu", "oanabratucolaborator"];

const FORM_STYLE_ID = "text-helper-swal-style";

// Injectat o singura data - da spatiere/etichete formularului din Swal, altfel
// campurile ies inghesuite (swal2-input/textarea n-au margine intre ele by default).
function injectFormStyles() {
	if (document.getElementById(FORM_STYLE_ID)) return;
	const style = document.createElement("style");
	style.id = FORM_STYLE_ID;
	style.textContent = `
		.text-helper-swal-form { text-align: left; }
		.text-helper-swal-form .form-group { margin-bottom: 18px; }
		.text-helper-swal-form .form-group:last-child { margin-bottom: 0; }
		.text-helper-swal-form label {
			display: block;
			margin-bottom: 6px;
			font-size: 13px;
			font-weight: 600;
			opacity: 0.75;
		}
		.text-helper-swal-form .swal2-input,
		.text-helper-swal-form .swal2-textarea {
			margin: 0;
			width: 100%;
			box-sizing: border-box;
			font-size: 14px;
		}
		.text-helper-swal-form .swal2-textarea {
			height: 280px;
			resize: vertical;
		}
	`;
	document.head.appendChild(style);
}

// Dark/light theming vine automat din styles/dark.css (.dark .swal2-popup),
// aici doar dam z-index peste Control Panel (999999) - fara asta popup-ul
// ar aparea sub bara laterala si ar parea "rupt".
const swalOverPanel = {
	width: "700px",
	willOpen: () => {
		const container = document.querySelector(".swal2-container");
		if (container) container.style.zIndex = "1000001";
	},
};

function readForm() {
	const title = document.getElementById("swal-th-title").value.trim();
	const text = document.getElementById("swal-th-text").value.trim();
	if (!title || !text) {
		Swal.showValidationMessage("Titlul și textul sunt necesare.");
		return false;
	}
	return { title, text };
}

export default function TextHelpers() {
	const { userData } = getUserData();
	const { isDarkMode } = useThemeMode();
	const [items, setItems] = useState([]);

	const allowed = userData?.admin === 2 || ALLOWED_USERNAMES.includes(userData?.username);

	useEffect(() => {
		if (!allowed) return;
		fetchWrapper
			.get("/api/admin/text-helpers")
			.then((data) => setItems(data || []))
			.catch((err) => console.error("Eroare la încărcarea textelor ajutătoare:", err));
	}, [allowed]);

	if (!allowed) return null;

	const handleAdd = async () => {
		const { value: formValues } = await Swal.fire({
			...swalOverPanel,
			title: "Text ajutător nou",
			html:
				'<div class="text-helper-swal-form">' +
				'<div class="form-group"><label>Titlu</label>' +
				'<input id="swal-th-title" class="swal2-input" placeholder=""></div>' +
				'<div class="form-group"><label>Text</label>' +
				'<textarea id="swal-th-text" class="swal2-textarea" placeholder="Textul complet..."></textarea></div>' +
				"</div>",
			didOpen: injectFormStyles,
			showCancelButton: true,
			confirmButtonText: "Salvează",
			cancelButtonText: "Anulează",
			preConfirm: readForm,
		});

		if (!formValues) return;

		try {
			const created = await fetchWrapper.post("/api/admin/text-helpers", formValues);
			setItems((prev) => [...prev, created].sort((a, b) => a.title.localeCompare(b.title)));
		} catch (err) {
			console.error("Eroare la salvarea textului ajutător:", err);
			Swal.fire({ ...swalOverPanel, width: undefined, icon: "error", title: "Eroare", text: String(err) });
		}
	};

	const handleOpen = async (id) => {
		const item = items.find((i) => i.id === id);
		if (!item) return;

		const result = await Swal.fire({
			...swalOverPanel,
			title: "Text ajutător",
			html:
				'<div class="text-helper-swal-form">' +
				'<div class="form-group"><label>Titlu</label>' +
				`<input id="swal-th-title" class="swal2-input" value="${escapeHtml(item.title)}"></div>` +
				'<div class="form-group"><label>Text</label>' +
				`<textarea id="swal-th-text" class="swal2-textarea">${escapeHtml(item.text)}</textarea></div>` +
				"</div>",
			didOpen: injectFormStyles,
			showCancelButton: true,
			showDenyButton: true,
			confirmButtonText: "Salvează modificările",
			denyButtonText: "Șterge",
			cancelButtonText: "Închide",
			preConfirm: readForm,
		});

		if (result.isConfirmed && result.value) {
			try {
				const updated = await fetchWrapper.put("/api/admin/text-helpers", { id: item.id, ...result.value });
				setItems((prev) =>
					[...prev.map((i) => (i.id === item.id ? updated : i))].sort((a, b) => a.title.localeCompare(b.title)),
				);
			} catch (err) {
				console.error("Eroare la editarea textului ajutător:", err);
				Swal.fire({ ...swalOverPanel, width: undefined, icon: "error", title: "Eroare", text: String(err) });
			}
			return;
		}

		if (result.isDenied) {
			const confirmDelete = await Swal.fire({
				...swalOverPanel,
				width: undefined,
				icon: "warning",
				title: `Ștergi "${item.title}"?`,
				text: "Nu poți reveni asupra acestei acțiuni.",
				showCancelButton: true,
				confirmButtonText: "Șterge",
				cancelButtonText: "Anulează",
				confirmButtonColor: "#d32f2f",
			});

			if (confirmDelete.isConfirmed) {
				try {
					await fetchWrapper.delete("/api/admin/text-helpers", { id: item.id });
					setItems((prev) => prev.filter((i) => i.id !== item.id));
				} catch (err) {
					console.error("Eroare la ștergerea textului ajutător:", err);
					Swal.fire({ ...swalOverPanel, width: undefined, icon: "error", title: "Eroare", text: String(err) });
				}
			}
		}
	};

	return (
		<Box sx={{ mb: 2 }}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
				<Typography component="label" fontWeight="bold" style={{ color: isDarkMode ? "#fff" : "#000" }}>
					Texte ajutătoare
				</Typography>
				<Tooltip title="Adaugă text nou">
					<IconButton
						size="small"
						onClick={handleAdd}
						sx={{
							bgcolor: "primary.main",
							color: "#fff",
							width: 26,
							height: 26,
							"&:hover": { bgcolor: "primary.dark" },
						}}
					>
						<AddIcon sx={{ fontSize: 16 }} />
					</IconButton>
				</Tooltip>
			</Stack>

			{items.length === 0 ? (
				<Typography variant="caption" color="text.secondary">
					Niciun text adăugat încă.
				</Typography>
			) : (
				<List dense disablePadding sx={{ maxHeight: 260, overflowY: "auto" }}>
					{items.map((item) => (
						<ListItemButton
							key={item.id}
							onClick={() => handleOpen(item.id)}
							sx={{ borderRadius: 1, py: 0.5, px: 1 }}
						>
							<ListItemText primary={item.title} primaryTypographyProps={{ fontSize: 14, noWrap: true }} />
						</ListItemButton>
					))}
				</List>
			)}

			<Divider sx={{ mt: 2 }} />
		</Box>
	);
}
